import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Eye, Tag,
  AlertCircle, Loader2, Trash2, TriangleAlert,
} from 'lucide-react';
import { get, del } from '@/lib/apiClient';
import { API_URL } from '@/_config';
import { useRegisterBreadcrumbTitle } from '@/context/BreadcrumbContext';

interface Lead {
  id: string;
  name: string;
  company: string;
  title: string;
  industry: string;
  location: string;
  email?: string | null;
  source: string;
  added: string;
}

interface Segment {
  id: string;
  name: string;
  lead_count: number;
  created_at: string;
}

const sourceBadge = (source: string): string => {
  const map: Record<string, string> = { LinkedIn: 'badge-blue', Apollo: 'badge-blue', 'Internal Database': 'badge-blue', Import: 'badge-gray' };
  return map[source] || 'badge-gray';
};
const sourceLabel = (source: string): string => source;

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

export default function SegmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const perPage = 25;

  // Selection state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // Confirmation dialog state
  const [confirmLeadIds, setConfirmLeadIds] = useState<string[] | null>(null);

  const { data: segData, isLoading: segLoading, isError: segError } = useQuery({
    queryKey: ['segment', id],
    queryFn: () => get<{ segment: Segment }>(API_URL.SEGMENTS.GET(id!)),
    enabled: !!id,
  });

  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['segment-leads', id, page],
    queryFn: () => get<{ leads: Lead[] }>(API_URL.SEGMENTS.LEADS(id!), { page, per_page: perPage }),
    enabled: !!id,
    placeholderData: (prev) => prev,
  });

  const segment: Segment | null = segData?.data?.segment ?? null;
  useRegisterBreadcrumbTitle(id, segment?.name);
  const leads: Lead[] = leadsData?.data?.leads ?? [];
  const total: number = (leadsData?.meta as any)?.total ?? 0;
  const lastPage: number = (leadsData?.meta as any)?.last_page ?? 1;
  const showingFrom = leads.length > 0 ? (page - 1) * perPage + 1 : 0;
  const showingTo = Math.min(page * perPage, total);

  const allChecked = leads.length > 0 && leads.every(l => selected.has(l.id));
  const someChecked = leads.some(l => selected.has(l.id));

  const toggleAll = () => {
    if (allChecked) {
      setSelected(prev => { const s = new Set(prev); leads.forEach(l => s.delete(l.id)); return s; });
    } else {
      setSelected(prev => { const s = new Set(prev); leads.forEach(l => s.add(l.id)); return s; });
    }
  };

  const toggleOne = (leadId: string) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(leadId) ? s.delete(leadId) : s.add(leadId);
      return s;
    });
  };

  // Called when user clicks delete — opens confirmation dialog
  const requestRemove = (leadIds: string[]) => {
    if (leadIds.length === 0) return;
    setConfirmLeadIds(leadIds);
  };

  // Called after user confirms — does the actual deletion
  const confirmRemove = async () => {
    if (!id || !confirmLeadIds) return;
    setDeleting(true);
    setConfirmLeadIds(null);
    try {
      await del(API_URL.SEGMENTS.REMOVE_LEADS(id), {
        data: { lead_ids: confirmLeadIds },
      });
      setSelected(prev => { const s = new Set(prev); confirmLeadIds.forEach(i => s.delete(i)); return s; });

      // If removing these leads empties the segment, delete the segment and go back
      const remainingCount = total - confirmLeadIds.length;
      if (remainingCount <= 0) {
        await del(API_URL.SEGMENTS.DELETE(id));
        queryClient.invalidateQueries({ queryKey: ['segments'] });
        navigate('/app/leads');
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['segment-leads', id] });
      queryClient.invalidateQueries({ queryKey: ['segment', id] });
    } finally {
      setDeleting(false);
    }
  };

  if (segLoading) {
    return (
      <div className="sdp-page sdp-center">
        <Loader2 size={22} className="sdp-spin" />
        <span style={{ fontSize: 15, color: 'var(--gray-500)' }}>Loading segment...</span>
      </div>
    );
  }

  if (segError || !segment) {
    return (
      <div className="sdp-page sdp-center">
        <AlertCircle size={32} color="var(--red, #ef4444)" />
        <p style={{ fontSize: 15, color: 'var(--gray-500)' }}>Segment not found.</p>
        <Link to="/app/leads" className="btn btn-secondary btn-sm">Back to Leads</Link>
      </div>
    );
  }

  return (
    <div className="sdp-page">
      <Link to="/app/leads" state={{ returnTab: 'My Segments' }} className="sdp-back">
        <ArrowLeft size={16} /> Back to Segments
      </Link>

      <div className="sdp-header">
        <div className="sdp-title-row">
          <Tag size={22} color="var(--sky)" />
          <h1 className="sdp-title">{segment.name}</h1>
        </div>
        <p className="sdp-meta">{segment.lead_count.toLocaleString()} leads · Created {new Date(segment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sdp-bulk-bar">
          <span className="sdp-bulk-count">{selected.size} lead{selected.size > 1 ? 's' : ''} selected</span>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => requestRemove(Array.from(selected))}
            disabled={deleting}
          >
            {deleting ? <Loader2 size={14} className="sdp-spin" /> : <Trash2 size={14} />}
            Remove from segment
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>
            Cancel
          </button>
        </div>
      )}

      {leadsLoading ? (
        <div className="sdp-center" style={{ padding: '48px 0' }}>
          <Loader2 size={20} className="sdp-spin" color="var(--gray-400)" />
        </div>
      ) : leads.length === 0 ? (
        <div className="sdp-empty">
          <Tag size={40} color="var(--gray-300)" />
          <p className="sdp-empty-title">No leads in this segment</p>
          <p className="sdp-empty-sub">Go to <Link to="/app/leads" className="sdp-link">All Leads</Link> and add leads to this segment.</p>
        </div>
      ) : (
        <>
          <div className="card sdp-card">
            <div className="sdp-table-wrap">
              <table className="sdp-table">
                <thead>
                  <tr className="sdp-thead-row">
                    <th className="sdp-th sdp-th-check">
                      <input
                        type="checkbox"
                        className="sdp-checkbox"
                        checked={allChecked}
                        ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                        onChange={toggleAll}
                        aria-label="Select all"
                      />
                    </th>
                    <th className="sdp-th sdp-th-name">Name</th>
                    <th className="sdp-th sdp-th-company">Company</th>
                    <th className="sdp-th sdp-th-title">Title</th>
                    <th className="sdp-th sdp-th-industry">Industry</th>
                    <th className="sdp-th sdp-th-location">Location</th>
                    <th className="sdp-th sdp-th-source">Source</th>
                    <th className="sdp-th sdp-th-added">Added</th>
                    <th className="sdp-th sdp-th-more"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id} className={`sdp-row${selected.has(lead.id) ? ' sdp-row--selected' : ''}`}>
                      <td className="sdp-td sdp-td-check">
                        <input
                          type="checkbox"
                          className="sdp-checkbox"
                          checked={selected.has(lead.id)}
                          onChange={() => toggleOne(lead.id)}
                          aria-label={`Select ${lead.name}`}
                        />
                      </td>
                      <td className="sdp-td">
                        <div className="sdp-name-cell">
                          <div className="sdp-avatar">{initials(lead.name)}</div>
                          <span className="sdp-name">{lead.name}</span>
                        </div>
                      </td>
                      <td className="sdp-td sdp-td-muted">{lead.company}</td>
                      <td className="sdp-td sdp-td-muted">{lead.title}</td>
                      <td className="sdp-td">
                        {lead.industry
                          ? <span className="badge badge-blue">{lead.industry}</span>
                          : <span className="sdp-td-muted">—</span>}
                      </td>
                      <td className="sdp-td sdp-td-muted">{lead.location}</td>
                      <td className="sdp-td">
                        <span className={`badge ${sourceBadge(lead.source)}`}>{sourceLabel(lead.source)}</span>
                      </td>
                      <td className="sdp-td sdp-td-muted sdp-td-added">{lead.added}</td>
                      <td className="sdp-td sdp-td-more">
                        <div className="sdp-actions">
                          <Link to={`/app/leads/${lead.id}`} className="sdp-detail-btn" aria-label="View details">
                            <Eye size={16} />
                          </Link>
                          <button
                            className="sdp-detail-btn sdp-delete-btn"
                            aria-label="Remove from segment"
                            onClick={() => requestRemove([lead.id])}
                            disabled={deleting}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="sdp-pagination">
            <p className="sdp-pag-count">
              {total > 0 ? `Showing ${showingFrom}-${showingTo} of ${total.toLocaleString()} leads` : 'No leads'}
            </p>
            {lastPage > 1 && (
              <div className="sdp-pag-controls">
                <button className="btn btn-secondary btn-sm sdp-pag-arrow" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft size={16} />
                </button>
                {page > 2 && <button className="sdp-pag-btn" onClick={() => setPage(1)}>1</button>}
                {page > 3 && <span className="sdp-pag-ellipsis">...</span>}
                {page > 1 && <button className="sdp-pag-btn" onClick={() => setPage(page - 1)}>{page - 1}</button>}
                <button className="sdp-pag-btn sdp-pag-btn--active">{page}</button>
                {page < lastPage && <button className="sdp-pag-btn" onClick={() => setPage(page + 1)}>{page + 1}</button>}
                {page < lastPage - 2 && <span className="sdp-pag-ellipsis">...</span>}
                {page < lastPage - 1 && <button className="sdp-pag-btn" onClick={() => setPage(lastPage)}>{lastPage}</button>}
                <button className="btn btn-secondary btn-sm sdp-pag-arrow" onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page === lastPage}>
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirmation dialog */}
      {confirmLeadIds && (
        <div className="sdp-confirm-overlay" onClick={() => setConfirmLeadIds(null)}>
          <div className="sdp-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="sdp-confirm-icon">
              <TriangleAlert size={28} color="var(--red, #ef4444)" />
            </div>
            <h3 className="sdp-confirm-title">Remove {confirmLeadIds.length === 1 ? 'lead' : `${confirmLeadIds.length} leads`}?</h3>
            <p className="sdp-confirm-body">
              {confirmLeadIds.length === total
                ? 'This will remove all leads and also delete the segment.'
                : `${confirmLeadIds.length === 1 ? 'This lead' : 'These leads'} will be removed from the segment.`}
            </p>
            <div className="sdp-confirm-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmLeadIds(null)}>
                Cancel
              </button>
              <button className="btn btn-danger btn-sm" onClick={confirmRemove} disabled={deleting}>
                {deleting ? <Loader2 size={14} className="sdp-spin" /> : <Trash2 size={14} />}
                {confirmLeadIds.length === total ? 'Remove & delete segment' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .sdp-page { padding: 24px; max-width: 1400px; margin: 0 auto; }
        .sdp-center { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 60vh; gap: 12px; }
        .sdp-spin { animation: sdp-spin 1s linear infinite; }
        @keyframes sdp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .sdp-back { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; color: var(--gray-500); margin-bottom: 20px; text-decoration: none; transition: color 0.15s; }
        .sdp-back:hover { color: var(--sky); }
        .sdp-header { margin-bottom: 24px; }
        .sdp-title-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
        .sdp-title { font-size: 26px; color: var(--navy); margin: 0; }
        .sdp-meta { font-size: 14px; color: var(--gray-500); margin: 0; }
        .sdp-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; gap: 12px; text-align: center; }
        .sdp-empty-title { font-size: 16px; font-weight: 600; color: var(--gray-600); margin: 0; }
        .sdp-empty-sub { font-size: 14px; color: var(--gray-400); margin: 0; }
        .sdp-link { color: var(--sky); text-decoration: none; }
        .sdp-link:hover { text-decoration: underline; }

        /* Bulk action bar */
        .sdp-bulk-bar { display: flex; align-items: center; gap: 10px; padding: 10px 16px; background: var(--sky-light, #EBF5FB); border: 1px solid var(--sky, #3b82f6); border-radius: 8px; margin-bottom: 12px; flex-wrap: wrap; }
        .sdp-bulk-count { font-size: 14px; font-weight: 500; color: var(--navy); flex: 1; }

        .sdp-card { padding: 0; overflow: hidden; margin-bottom: 0; }
        .sdp-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .sdp-table { width: 100%; border-collapse: collapse; font-size: 14px; min-width: 980px; }
        .sdp-thead-row { border-bottom: 1px solid var(--gray-200); background: var(--gray-50); }
        .sdp-th { text-align: left; padding: 12px 16px; color: var(--gray-500); font-weight: 500; font-size: 12px; text-transform: uppercase; white-space: nowrap; }
        .sdp-th-check { width: 40px; padding-right: 0; }
        .sdp-th-name { min-width: 160px; }
        .sdp-th-company { min-width: 130px; }
        .sdp-th-title { min-width: 140px; }
        .sdp-th-industry { min-width: 130px; }
        .sdp-th-location { min-width: 140px; }
        .sdp-th-source { min-width: 100px; }
        .sdp-th-added { min-width: 110px; }
        .sdp-th-more { width: 72px; }
        .sdp-row { border-bottom: 1px solid var(--gray-100); transition: background 0.1s; }
        .sdp-row:hover { background: var(--gray-50); }
        .sdp-row--selected { background: var(--sky-light, #EBF5FB) !important; }
        .sdp-td { padding: 12px 16px; }
        .sdp-td-check { width: 40px; padding-right: 0; }
        .sdp-td-muted { color: var(--gray-500); white-space: nowrap; }
        .sdp-td-added { font-size: 13px; }
        .sdp-td-more { width: 72px; }
        .sdp-name-cell { display: flex; align-items: center; gap: 10px; }
        .sdp-avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--sky-light); color: var(--sky); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; flex-shrink: 0; }
        .sdp-name { font-weight: 500; white-space: nowrap; color: var(--gray-900); }
        .sdp-actions { display: flex; align-items: center; gap: 2px; }
        .sdp-detail-btn { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 6px; color: var(--gray-400); transition: color 0.15s, background 0.15s; text-decoration: none; background: transparent; border: none; cursor: pointer; padding: 0; }
        .sdp-detail-btn:hover { color: var(--sky); background: var(--sky-light, #EBF5FB); }
        .sdp-delete-btn:hover { color: var(--red, #ef4444) !important; background: #fef2f2 !important; }
        .sdp-checkbox { width: 16px; height: 16px; cursor: pointer; accent-color: var(--sky); }

        .sdp-pagination { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; flex-wrap: wrap; gap: 12px; }
        .sdp-pag-count { font-size: 14px; color: var(--gray-500); }
        .sdp-pag-controls { display: flex; gap: 6px; align-items: center; }
        .sdp-pag-btn { min-width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; background: transparent; color: var(--gray-500); font-size: 14px; font-weight: 500; cursor: pointer; border: none; padding: 0 10px; }
        .sdp-pag-btn:hover { background: var(--gray-100); }
        .sdp-pag-btn--active { background: var(--sky); color: white; }
        .sdp-pag-btn--active:hover { background: var(--sky); }
        .sdp-pag-ellipsis { color: var(--gray-500); font-size: 14px; padding: 0 4px; }
        .sdp-pag-arrow { padding: 8px; }
        @media (max-width: 768px) { .sdp-page { padding: 16px; } }

        /* Confirmation modal */
        .sdp-confirm-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; }
        .sdp-confirm-modal { background: white; border-radius: 12px; padding: 28px 28px 24px; max-width: 380px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.18); text-align: center; }
        .sdp-confirm-icon { margin-bottom: 12px; }
        .sdp-confirm-title { font-size: 17px; font-weight: 600; color: var(--navy); margin: 0 0 8px; }
        .sdp-confirm-body { font-size: 14px; color: var(--gray-500); margin: 0 0 24px; line-height: 1.5; }
        .sdp-confirm-actions { display: flex; justify-content: center; gap: 10px; }
      `}</style>
    </div>
  );
}
